﻿import { useColorMode } from "@kobalte/core/color-mode";
import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import type { ChartData, ChartOptions } from "chart.js";
import type { JSX } from "solid-js";
import { createMemo, createSignal, For, onMount, Show } from "solid-js";
import { toast } from "solid-sonner";
import { z } from "zod";
import {
  IconChartColumn,
  IconCircleDollarSign,
  IconHash,
  IconListTree,
  IconPencil,
  IconTrash,
} from "~/components/icons";
import JournalEntryTable from "~/components/journal-entry/table/journal-entry-table";
import { NotFound } from "~/components/not-found";
import { StandardRouteDetailCard } from "~/components/standard-route/detail/detail-card";
import { StandardRouteDetailEditCard } from "~/components/standard-route/detail/edit-card";
import { StandardRouteDetailHeader } from "~/components/standard-route/detail/header";
import {
  StandardRouteDetailGrid,
  StandardRouteDetailLeftColumn,
  StandardRouteDetailMode,
  StandardRouteDetailRightCard,
  StandardRouteDetailRightCardHeader,
  StandardRouteDetailRightColumn,
  StandardRouteDetailTabs,
  StandardRouteDetailTabsContent,
  StandardRouteDetailTabsStack,
} from "~/components/standard-route/detail/layout";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { CardContent } from "~/components/ui/card";
import { LineChart } from "~/components/ui/charts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "~/components/ui/dropdown-menu";
import { Col, Grid } from "~/components/ui/grid";
import { MultiSelect } from "~/components/ui/multi-select";
import { TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  TextField,
  TextFieldErrorMessage,
  TextFieldInput,
  TextFieldLabel,
} from "~/components/ui/text-field";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { deleteAccounts } from "~/features/account/account.api";
import { useAccountForm } from "~/features/account/account.form";
import {
  AccountBalanceHistoryResource,
  AccountDetailResource,
  AccountsResource,
} from "~/features/account/account.resource";
import {
  AccountTypes,
  computeAccountBalance,
  type Account,
  type AccountType,
} from "~/features/account/account.schema";
import type { AccountsItem } from "~/features/account/account.server";
import { getAccountsDetailPageData } from "~/features/account/accounts-detail-page";
import {
  businessDetailResource,
  businessesResource,
} from "~/features/business/business.resource";
import { JournalEntriesResource } from "~/features/journal-entry/journal-entry.resource";
import { invalidateReportsQueries } from "~/features/reports/reports.invalidation";
import { withBusinessGuard } from "~/lib/route-guards";
import { resolveServerErrorBody } from "~/lib/server-fn-error";
import { formatCurrency } from "~/lib/utils";

export const Route = createFileRoute(
  "/_dashboard/dashboard/accounts/$accountId",
)({
  staticData: {
    breadcrumb: (m) => m.loaderData?.account?.name ?? "Account",
  },
  validateSearch: z.object({
    mode: z.enum(["edit"]).optional(),
  }),
  // Route-specific 404 message (takes precedence over the dashboard-wide
  // notFoundComponent). Shown when an account doesn't exist — e.g. it was
  // deleted, or an AI agent linked here by mistake.
  notFoundComponent: () => (
    <NotFound
      title="Account not found"
      message="This account doesn't exist. It may have been deleted, or an agent may have linked you here by mistake."
      homeTo="/dashboard/accounts"
      homeLabel="Go to accounts"
    />
  ),
  ...withBusinessGuard(),
  loader: async ({ params, context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;

    if (params.accountId === "new") {
      // For "new" accounts, we still fetch business detail + accounts list
      // but set account detail to null without a server call.
      const data = await getAccountsDetailPageData({
        data: { businessId, accountId: "new" },
      });
      businessDetailResource.seed(
        context.queryClient,
        businessId,
        data.businessDetail,
      );
      context.queryClient.setQueryData(
        AccountDetailResource.key({ businessId, id: "new" }),
        null,
      );
      AccountsResource.seed(
        context.queryClient,
        { businessId, limit: 200 },
        data.accountsList,
      );
      return {
        businessId,
        activeBusiness: data.businessDetail,
        account: null,
        accounts: data.accountsList,
        _aggregate: data,
      };
    }

    const data = await getAccountsDetailPageData({
      data: { businessId, accountId: params.accountId },
    });
    businessDetailResource.seed(
      context.queryClient,
      businessId,
      data.businessDetail,
    );
    if (data.accountDetail) {
      AccountDetailResource.seed(
        context.queryClient,
        { businessId, id: params.accountId },
        data.accountDetail,
      );
    }
    AccountsResource.seed(
      context.queryClient,
      { businessId, limit: 200 },
      data.accountsList,
    );

    // Prefetch the detail page's component-level queries so they resolve from
    // cache on mount instead of flashing loading states. These run during the
    // route preload (hover), so by the time the user clicks, the balance
    // history chart and journal entries tab are already cached. They're kicked
    // off in parallel and NOT awaited — they must not block the loader's
    // critical path or SSR.
    void context.queryClient.prefetchQuery(
      AccountBalanceHistoryResource.options({
        businessId,
        accountId: params.accountId,
        period: "monthly",
      }),
    );
    void context.queryClient.prefetchQuery(
      JournalEntriesResource.options({
        businessId,
        accountId: params.accountId,
        limit: 20,
        sortBy: "date",
        sortDir: "desc",
      }),
    );

    return {
      businessId,
      activeBusiness: data.businessDetail,
      account: data.accountDetail,
      accounts: data.accountsList,
      _aggregate: data,
    };
  },
  component: AccountDetailPage,
});

type Mode = "view" | "edit";

function AccountDetailPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = Route.useParams();
  const data = Route.useLoaderData();

  businessDetailResource.seedIfEmpty(
    qc,
    data().businessId,
    data().activeBusiness,
  );
  if (data().account) {
    AccountDetailResource.seedIfEmpty(
      qc,
      { businessId: data().businessId, id: params().accountId },
      data().account!,
    );
  } else if (params().accountId === "new") {
    // Seed null into the component QueryClient before createQuery runs.
    // Without this, accountQuery with enabled:false has status "pending" in the
    // component-side QueryClient, which causes TanStack Start's deferred SSR
    // stream to wait forever (60s timeout) on a hard refresh.
    const newAccountKey = AccountDetailResource.key({
      businessId: data().businessId,
      id: params().accountId,
    });
    if (qc.getQueryData(newAccountKey) === undefined) {
      qc.setQueryData(newAccountKey, null);
    }
  }
  AccountsResource.seedIfEmpty(
    qc,
    { businessId: data().businessId, limit: 200 },
    data().accounts,
  );

  const isNew = () => params().accountId === "new";
  const search = Route.useSearch();
  const initialMode: Mode = search().mode === "edit" ? "edit" : "view";
  const [mode, setMode] = createSignal<Mode>(initialMode);

  onMount(() => {
    if (initialMode === "edit") {
      const url = new URL(window.location.href);
      url.searchParams.delete("mode");
      window.history.replaceState(window.history.state, "", url.toString());
    }
  });

  const businessQuery = createQuery(() => ({
    ...businessDetailResource.options(data().businessId),
    placeholderData: keepPreviousData,
  }));

  const accountQuery = createQuery(() => ({
    ...AccountDetailResource.options(
      { businessId: data().businessId, id: params().accountId },
      !isNew(),
    ),
    placeholderData: keepPreviousData,
  }));

  const accountsQuery = AccountsResource.use(() => ({
    businessId: data().businessId,
    limit: 200,
  }));

  const classes = () => businessQuery.data?.classes ?? [];
  const allAccounts = () => accountsQuery.data?.items ?? [];

  return (
    <div class="flex flex-1 flex-col px-4 sm:px-6 pb-3 pt-10">
      <Show when={isNew()}>
        <AccountEdit
          account={null}
          businessId={data().businessId}
          classes={classes()}
          allAccounts={allAccounts()}
          onCancel={() => navigate({ to: "/dashboard/accounts" })}
          onSaved={() => navigate({ to: "/dashboard/accounts" })}
        />
      </Show>
      <Show when={!isNew()}>
        <Show
          when={accountQuery.data}
          fallback={
            <div role="status" class="w-full animate-pulse">
              <div class="h-150 bg-muted rounded-lg w-full" />
            </div>
          }
        >
          {(account) => (
            <Show
              when={mode() === "edit"}
              fallback={
                <AccountView
                  account={account()}
                  businessId={data().businessId}
                  allAccounts={allAccounts()}
                  setMode={setMode}
                  onDeleted={() => {
                    AccountDetailResource.remove(qc, {
                      businessId: data().businessId,
                      id: params().accountId,
                    });
                    // Remove the list cache so the table doesn't flash the
                    // deleted row before a background refetch.
                    qc.removeQueries({
                      queryKey: ["account", data().businessId],
                    });
                    // Deleting an account changes the chart of accounts that
                    // feeds the reports.
                    void invalidateReportsQueries(qc, data().businessId);
                    navigate({ to: "/dashboard/accounts" });
                  }}
                />
              }
            >
              <AccountEdit
                account={account()}
                businessId={data().businessId}
                classes={classes()}
                allAccounts={allAccounts()}
                onCancel={() => setMode("view")}
                onSaved={() => {
                  AccountDetailResource.invalidate(qc, {
                    businessId: data().businessId,
                    id: params().accountId,
                  });
                  setMode("view");
                }}
              />
            </Show>
          )}
        </Show>
      </Show>
    </div>
  );
}

function DeleteAccountDialog(props: {
  account: Account;
  businessId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const [isDeleting, setIsDeleting] = createSignal(false);

  async function handleDelete() {
    setIsDeleting(true);
    try {
      const result = await deleteAccounts({
        data: { id: props.account.id, businessId: props.businessId },
      });
      const errBody = await resolveServerErrorBody(result);
      if (errBody) {
        toast.error(errBody.error ?? "Failed to delete account");
        return;
      }
      props.onDeleted();
    } catch {
      toast.error("Failed to delete account");
    } finally {
      setIsDeleting(false);
      props.onOpenChange(false);
    }
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Account</DialogTitle>
          <DialogDescription>
            This will permanently delete{" "}
            <span class="font-semibold">"{props.account.name}"</span>. This
            action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => props.onOpenChange(false)}
            disabled={isDeleting()}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting()}
          >
            <IconTrash class="mr-2 size-4" />
            {isDeleting() ? "Deleting..." : "Delete Account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -- Account History Chart -----------------------------------------------------

const HISTORY_COLOR = { hex: "#5B8DEF", rgb: "91,141,239" };

type HistoryPeriod = "monthly" | "yearly";

function AccountHistoryChart(props: { businessId: string; accountId: string }) {
  const { colorMode } = useColorMode();
  const [period, setPeriod] = createSignal<HistoryPeriod>("monthly");

  // NOTE: rely on the resource's `placeholderData` (empty points) rather than
  // overriding with `keepPreviousData`. This embedded chart is NOT prefetched
  // in the loader, so during SSR the query must render from placeholder data —
  // otherwise the enabled query suspends the route's Suspense boundary and
  // blocks the initial page load (see SSR hydration rules). The placeholder
  // keeps SSR + client first render in sync; real data fetches client-side.
  const historyQuery = createQuery(() => ({
    ...AccountBalanceHistoryResource.options({
      businessId: props.businessId,
      accountId: props.accountId,
      period: period(),
    }),
  }));

  const chartOptions = createMemo<ChartOptions>(() => {
    const dark = colorMode() === "dark";
    const tickColor = dark ? "hsl(240, 5%, 64.9%)" : "hsl(240, 3.8%, 30%)";
    const gridColor = dark
      ? "rgba(255,255,255,0.07)"
      : "rgba(128,128,128,0.08)";
    const tooltipBg = dark ? "rgba(50,50,60,0.95)" : "rgba(255,255,255,0.96)";
    const tooltipTextColor = dark ? "#fff" : "hsl(240, 10%, 10%)";
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 0, bottom: 0, left: 0, right: 4 } },
      scales: {
        y: {
          display: true,
          border: { display: false },
          grid: { color: gridColor },
          ticks: {
            maxTicksLimit: 4,
            color: tickColor,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            font: { size: 11, family: "'DM Mono', monospace" } as any,
            callback: (v: number | string) => {
              const n = +v;
              if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
              if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
              return `$${n}`;
            },
          },
        },
        x: {
          border: { display: false },
          grid: { display: false },
          ticks: {
            color: tickColor,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            font: { size: 11, family: "'DM Mono', monospace" } as any,
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: period() === "monthly" ? 6 : 10,
          },
        },
      },
      plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: tooltipBg,
          titleColor: tooltipTextColor,
          bodyColor: tooltipTextColor,
          borderColor: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
          borderWidth: 1,
          padding: 8,
          cornerRadius: 6,
          callbacks: {
            label: (ctx) =>
              ` Net: $${Math.abs(ctx.parsed.y ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
          },
        },
      },
    };
  });

  const chartData = createMemo<ChartData>(() => {
    const points = historyQuery.data?.points ?? [];
    return {
      labels: points.map((p) => p.label),
      datasets: [
        {
          label: "Net activity",
          // abs() so credit-normal accounts (liabilities/equity/revenue) and
          // net-negative periods plot as positive magnitudes.
          data: points.map((p) => Math.abs(p.net)),
          borderColor: HISTORY_COLOR.hex,
          backgroundColor: `rgba(${HISTORY_COLOR.rgb},0.12)`,
          fill: true,
          tension: 0.3,
          pointRadius: 2,
          pointHoverRadius: 4,
          borderWidth: 2,
        },
      ],
    };
  });

  const hasData = () => (historyQuery.data?.points?.length ?? 0) > 0;
  const latestNet = () =>
    Math.abs(
      historyQuery.data?.points?.[historyQuery.data.points.length - 1]?.net ??
        0,
    );

  const periodOptions: { label: string; value: HistoryPeriod }[] = [
    { label: "Monthly", value: "monthly" },
    { label: "Yearly", value: "yearly" },
  ];

  return (
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="font-mono text-[26px] font-medium tracking-[-0.02em]">
            {formatCurrency(latestNet())}
          </span>
          <span class="text-[11px] tracking-[0.04em] text-muted-foreground">
            {period() === "monthly" ? "latest month" : "latest year"}
          </span>
        </div>

        {/* Monthly / Yearly toggle */}
        <div class="flex items-center gap-0.5 rounded-lg border p-0.5">
          <For each={periodOptions}>
            {(opt) => (
              <button
                type="button"
                onClick={() => setPeriod(opt.value)}
                classList={{
                  "rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors": true,
                  "bg-accent text-accent-foreground shadow-xs":
                    period() === opt.value,
                  "text-muted-foreground hover:text-foreground":
                    period() !== opt.value,
                }}
              >
                {opt.label}
              </button>
            )}
          </For>
        </div>
      </div>

      <Show
        when={hasData()}
        fallback={
          <div class="flex h-72 w-full items-center justify-center text-sm text-muted-foreground">
            No journal entries yet for this account.
          </div>
        }
      >
        <div class="h-72 w-full">
          <LineChart data={chartData()} options={chartOptions()} />
        </div>
      </Show>
    </div>
  );
}

function AccountView(props: {
  account: Account;
  businessId: string;
  allAccounts: Account[];
  setMode: (m: Mode) => void;
  onDeleted: () => void;
}) {
  const [deleteOpen, setDeleteOpen] = createSignal(false);

  const decodeType = (t: AccountType | undefined) =>
    t ? (AccountTypes.find((a) => a.value === t)?.label ?? t) : "N/A";

  const decodeParent = (parentId: string | undefined) =>
    parentId
      ? (props.allAccounts.find((a) => a.id === parentId)?.name ?? parentId)
      : "N/A";

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value ?? 0);

  const computedBalance = () => computeAccountBalance(props.account);

  return (
    <div class="space-y-6">
      <StandardRouteDetailHeader
        icon={<IconListTree class="size-5" />}
        title={props.account.name}
        badge={decodeType(props.account.type) ?? undefined}
        menuContent={
          <>
            <DropdownMenuItem onClick={() => props.setMode("edit")}>
              <IconPencil class="size-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              class="text-destructive focus:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <IconTrash class="size-4" />
              Delete
            </DropdownMenuItem>
          </>
        }
      />

      <StandardRouteDetailMode mode="content">
        <StandardRouteDetailGrid>
          {/* Left Column */}
          <StandardRouteDetailLeftColumn>
            <div class="space-y-6">
              <StandardRouteDetailCard
                icon={<IconHash class="size-5" />}
                title="Account"
                contentClass=""
                action={
                  <Tooltip>
                    <TooltipTrigger
                      as={Button<"button">}
                      variant="ghost"
                      size="icon"
                      class="size-7 text-muted-foreground hover:text-foreground"
                      aria-label="Edit account"
                      onClick={() => props.setMode("edit")}
                    >
                      <IconPencil class="size-4" />
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                }
              >
                {/* Balance is the single most important accounting fact — make it the hero */}
                <div class="pb-5">
                  <p class="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    Balance
                  </p>
                  <p class="mt-1.5 text-3xl font-semibold tabular-nums tracking-tight">
                    {formatCurrency(computedBalance())}
                  </p>
                </div>

                <div class="divide-y divide-border border-t">
                  <InfoRow label="Code" value={props.account.code} />
                  <InfoRow
                    label="Type"
                    value={decodeType(props.account.type)}
                  />
                  <InfoRow
                    label="Parent"
                    value={decodeParent(props.account.parentAccountId)}
                  />
                </div>
                <Show when={props.account.class}>
                  <div class="pt-5">
                    <p class="mb-2 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                      Class
                    </p>
                    <div class="flex flex-wrap gap-1.5">
                      <Badge variant="outline" class="font-normal">
                        {props.account.class}
                      </Badge>
                    </div>
                  </div>
                </Show>
              </StandardRouteDetailCard>
            </div>
          </StandardRouteDetailLeftColumn>

          {/* Right Column */}
          <StandardRouteDetailRightColumn>
            <StandardRouteDetailRightCard>
              <StandardRouteDetailRightCardHeader>
                <StandardRouteDetailTabs
                  defaultValue="journal-entries"
                  class="flex flex-col"
                >
                  <TabsList class="w-full">
                    <TabsTrigger value="journal-entries" class="flex-1">
                      <IconCircleDollarSign class="mr-2 size-4" />
                      <span class="hidden sm:inline">Journal Entries</span>
                    </TabsTrigger>
                    <TabsTrigger value="history" class="flex-1">
                      <IconChartColumn class="mr-2 size-4" />
                      <span class="hidden sm:inline">History</span>
                    </TabsTrigger>
                  </TabsList>

                  <StandardRouteDetailTabsStack>
                    <StandardRouteDetailTabsContent
                      value="journal-entries"
                      class="overflow-auto"
                      forceMount
                    >
                      <JournalEntryTable
                        businessId={props.businessId}
                        accountId={props.account.id}
                      />
                    </StandardRouteDetailTabsContent>

                    <StandardRouteDetailTabsContent
                      value="history"
                      class="overflow-auto"
                      forceMount
                    >
                      <CardContent class="px-0">
                        <AccountHistoryChart
                          businessId={props.businessId}
                          accountId={props.account.id}
                        />
                      </CardContent>
                    </StandardRouteDetailTabsContent>
                  </StandardRouteDetailTabsStack>
                </StandardRouteDetailTabs>
              </StandardRouteDetailRightCardHeader>
            </StandardRouteDetailRightCard>
          </StandardRouteDetailRightColumn>
        </StandardRouteDetailGrid>
      </StandardRouteDetailMode>

      <DeleteAccountDialog
        account={props.account}
        businessId={props.businessId}
        open={deleteOpen()}
        onOpenChange={setDeleteOpen}
        onDeleted={props.onDeleted}
      />
    </div>
  );
}

// -- Edit / Create -------------------------------------------------------------

function AccountEdit(props: {
  account: AccountsItem | null;
  businessId: string;
  classes: { id: string; name: string }[];
  allAccounts: Account[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const isNew = () => props.account === null;
  const [serverError, setServerError] = createSignal("");

  const form = useAccountForm(props.businessId, props.account, {
    onSuccess: () => {
      toast.success(
        isNew()
          ? "Account created successfully"
          : "Account updated successfully",
      );
      props.onSaved();
    },
    onError: (e) =>
      setServerError(
        e instanceof Error ? e.message : "An unexpected error occurred",
      ),
  });

  return (
    <StandardRouteDetailEditCard
      title={isNew() ? "Add Account" : "Edit Account"}
      description={
        isNew()
          ? "Create a new chart of accounts entry."
          : "Update the account details. Changes apply immediately on save."
      }
      formId="account-edit-form"
      onCancel={props.onCancel}
      saveLabel={isNew() ? "Add Account" : "Save Changes"}
    >
      <form
        id="account-edit-form"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Grid cols={1} colsMd={2} colsLg={3} class="gap-4">
          <Col>
            <form.Field name="parentAccountId">
              {(field) => (
                <FormField label="Parent Account" field={field()}>
                  <MultiSelect
                    placeholder="Select parent account..."
                    searchPlaceholder="Search accounts..."
                    options={props.allAccounts
                      .filter((a) => a.id !== props.account?.id)
                      .map((a) => ({
                        label: `${a.code} - ${a.name}`,
                        value: a.id,
                      }))}
                    value={
                      field().state.value != null
                        ? [field().state.value as string]
                        : []
                    }
                    onChange={(vals) =>
                      field().handleChange(vals[0] ?? undefined)
                    }
                    multiple={false}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="type">
              {(field) => (
                <FormField label="Type" required field={field()}>
                  <MultiSelect
                    placeholder="Select type..."
                    searchPlaceholder="Search types..."
                    options={AccountTypes}
                    value={field().state.value ? [field().state.value] : []}
                    onChange={(vals) =>
                      field().handleChange((vals[0] as AccountType) ?? "asset")
                    }
                    multiple={false}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="classId">
              {(field) => (
                <FormField label="Class" field={field()}>
                  <MultiSelect
                    placeholder="Select a class..."
                    searchPlaceholder="Search classes..."
                    options={props.classes.map((c) => ({
                      label: c.name,
                      value: c.id,
                    }))}
                    value={
                      field().state.value != null
                        ? [field().state.value as string]
                        : []
                    }
                    onChange={(vals) =>
                      field().handleChange(vals[0] ?? undefined)
                    }
                    multiple={false}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col>
            <form.Field name="code">
              {(field) => (
                <FormField label="Code" required field={field()}>
                  <TextFieldInput
                    type="text"
                    placeholder="e.g. 1000"
                    value={field().state.value}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col span={1} spanLg={2}>
            <form.Field name="name">
              {(field) => (
                <FormField label="Name" required field={field()}>
                  <TextFieldInput
                    type="text"
                    placeholder="Account name"
                    value={field().state.value}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
          <Col span={1} spanLg={3}>
            <form.Field name="description">
              {(field) => (
                <FormField label="Description" field={field()}>
                  <TextFieldInput
                    type="text"
                    placeholder="Optional description"
                    value={field().state.value ?? ""}
                    onInput={(e) => field().handleChange(e.currentTarget.value)}
                    onBlur={field().handleBlur}
                  />
                </FormField>
              )}
            </form.Field>
          </Col>
        </Grid>

        <Show when={serverError()}>
          <p class="mt-4 text-sm text-destructive">{serverError()}</p>
        </Show>
      </form>
    </StandardRouteDetailEditCard>
  );
}

// -- Helpers -------------------------------------------------------------------

function InfoRow(props: { label: string; value: string | null | undefined }) {
  return (
    <div class="flex items-center justify-between py-3.5">
      <p class="text-sm text-muted-foreground">{props.label}</p>
      <h6 class="text-sm font-semibold">{props.value || "N/A"}</h6>
    </div>
  );
}

function FormField(props: {
  label: string;
  required?: boolean;
  field: {
    state: { meta: { errors: (string | { message: string } | undefined)[] } };
  };
  children: JSX.Element;
}) {
  const hasError = () => props.field.state.meta.errors.length > 0;
  const errorMessage = () => {
    const e = props.field.state.meta.errors[0];
    if (!e) return undefined;
    return typeof e === "string" ? e : e.message;
  };
  return (
    <TextField validationState={hasError() ? "invalid" : "valid"}>
      <TextFieldLabel>
        {props.label}
        <Show when={props.required}>
          <span class="text-destructive ml-0.5">*</span>
        </Show>
      </TextFieldLabel>
      {props.children}
      <TextFieldErrorMessage>{errorMessage()}</TextFieldErrorMessage>
    </TextField>
  );
}
